import { Fragment, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowRight, Bot, Check, Clock, Copy, LogOut, Phone, PhoneForwarded, Siren, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../../__generated__/schema-lite';
import { useAccountBase, useCurrentAccount } from '../../account/AccountContext';
import { useLogout } from '../../hooks/useLogout';
import { translateError } from '../../lib/translate-error';

/** The onboarding wizard (§3-А). It appears once, after a new customer is approved,
 *  and its job is the one thing without which nothing else works: get the line a
 *  number and the customer's forwarding pointed at it. Everything after that is
 *  optional polish the customer can also change later from settings.
 *
 *  The wizard is a guided VIEW over AssistantConfig + PhoneNumber + OnboardingProgress
 *  — it does not keep its own copy of anything. That is what makes "change it later"
 *  free: the settings screens write the same rows. Only three of its actions are
 *  procedures (buying a number, skipping a step, finishing); the rest are plain
 *  auto-REST writes into the rows `provisionNumber` scaffolds.
 *
 *  The five steps mirror the OnboardingStep enum. NUMBER and FORWARDING_SETUP are
 *  mandatory — the wizard cannot advance past them and cannot finish without them —
 *  and the server refuses to skip or complete around them, so the buttons here are
 *  the courteous half of a rule the API enforces anyway. */

type CarrierRow = {
  id: string;
  carrier: string;
  label: string;
  enableCode: string;
  disableCode: string;
  statusCode: string | null;
};
type VoiceRow = { id: string; voiceId: string; label: string; language: string | null };
type ConfigRow = {
  greetingName: string | null;
  greetingScript: string | null;
  voiceId: string | null;
  emergencyTopics: string[];
  autoSwitchEnglish: boolean;
  ownNumber: string | null;
  carrier: string | null;
  forwardingConfirmedAt: string | null;
  customInstructions: string | null;
};

type PhoneRow = { id: string; e164: string };

// In the OnboardingStep order. Index into this is the step the wizard is on, and the
// enum name is what `skipOnboardingStep` is called with.
const STEPS = [
  'NUMBER',
  'FORWARDING_SETUP',
  'AGENT_PERSONALIZATION',
  'EMERGENCY_TOPICS',
  'TEST_CALL',
] as const;

// The top stepper — icon + label per actionable step, same icons and labels as
// the "This line" section in Settings (settings.steps.*). Indices line up with
// STEPS 0–3; the final TEST_CALL step is the "done" screen, not a nav item.
const STEP_NAV = [
  { icon: Phone, label: 'settings.steps.number' },
  { icon: PhoneForwarded, label: 'settings.steps.forwarding' },
  { icon: Bot, label: 'settings.steps.assistant' },
  { icon: Siren, label: 'settings.steps.topics' },
] as const;

// The `…*20#` segment of the carrier code — how many seconds the phone rings
// before an unanswered call is redirected to the assistant. Networks usually
// accept 5–30s in 5-second steps; the field lets the customer type any value.
const DEFAULT_FORWARD_SECONDS = 20;

export default function OnboardingPage() {
  const account = useCurrentAccount();
  const base = useAccountBase();
  const crud = useClientQueries(schema);
  const [params] = useSearchParams();
  const accountId = account.accountId;

  // Edit mode: an OWNER/ADMIN reopening the wizard from Settings to change a step
  // they already completed. Without it a fully-set-up line bounces straight to the
  // app (below), which is right for an accidental visit but wrong for a deliberate
  // "edit my setup". `?step=<n>` opens directly on that step.
  const canEdit = account.myRole === 'OWNER' || account.myRole === 'ADMIN';
  const editMode = params.get('edit') === '1' && canEdit;
  const stepParam = params.get('step');
  const stepRaw = stepParam ? Number(stepParam) : NaN;
  const initialStep =
    Number.isInteger(stepRaw) && stepRaw >= 0 && stepRaw < STEPS.length ? stepRaw : null;

  const configQ = crud.assistantConfig.useFindFirst({ where: { accountId } });
  const carriersQ = crud.carrierForwardingCode.useFindMany({
    where: { country: 'BG', active: true },
    orderBy: { carrier: 'asc' },
  });
  const voicesQ = crud.voice.useFindMany({ where: { active: true }, orderBy: { label: 'asc' } });

  // A line that is already set up has no wizard to run — send them to the app,
  // UNLESS they came here deliberately to edit. This is also what a delegate who
  // typed the URL hits: both gates are false for them, so they bounce straight back.
  if (!account.needsOnboarding && !account.setupUnfinished && !editMode)
    return <Navigate to={base} replace />;

  if (configQ.isLoading || carriersQ.isLoading || voicesQ.isLoading) return null;

  return (
    <Wizard
      accountId={accountId}
      base={base}
      initialStep={initialStep}
      initialConfig={(configQ.data as ConfigRow | null) ?? null}
      carriers={(carriersQ.data as CarrierRow[] | undefined) ?? []}
      voices={(voicesQ.data as VoiceRow[] | undefined) ?? []}
    />
  );
}

function Wizard({
  accountId,
  base,
  initialStep,
  initialConfig,
  carriers,
  voices,
}: {
  accountId: string;
  base: string;
  initialStep: number | null;
  initialConfig: ConfigRow | null;
  carriers: CarrierRow[];
  voices: VoiceRow[];
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useLogout();
  const queryClient = useQueryClient();
  const crud = useClientQueries(schema);

  // The shadow number, read live: a PLATFORM_ADMIN assigns it from the number pool,
  // so until then step 0 is a "waiting" screen. Steps 1+ need it for the forwarding code.
  const phoneQ = crud.phoneNumber.useFindFirst({
    where: { accountId, status: 'ACTIVE', releasedAt: null },
    orderBy: { purchasedAt: 'desc' },
  });
  const shadow = (phoneQ.data as PhoneRow | null | undefined) ?? null;

  const updateConfig = crud.assistantConfig.useUpdate();
  // The own-number step can run before any number is assigned, when no
  // AssistantConfig row exists yet (it is scaffolded on number assignment). Upsert
  // so that first save creates the row instead of failing an update.
  const upsertConfig = crud.assistantConfig.useUpsert();
  const skip = crud.$procs.skipOnboardingStep.useMutation();
  const complete = crud.$procs.completeOnboarding.useMutation();
  // Fires the durable `voice/agent.sync` — pushes the saved config to the linked
  // ElevenLabs agent (no-op until an admin links one). Runs through Inngest so it
  // shows in the admin monitor alongside the other runs.
  const agentSync = crud.$procs.agentSync.useMutation();
  const busy =
    updateConfig.isPending || upsertConfig.isPending || skip.isPending || complete.isPending;

  // Once the customer manually jumps to a step (via the progress dots), stop the
  // "a number arrived → advance off the waiting screen" auto-jump from yanking them
  // back — otherwise the mandatory steps could never be revisited/edited.
  const navigatedRef = useRef(false);

  // Resume where the mandatory work left off. Step 0 is the customer entering THEIR
  // OWN number (what we need to know their line); step 1 is forwarding, which needs
  // the admin-assigned shadow number and shows a waiting state until it exists. So:
  // no own number → step 0; own number but forwarding not confirmed → step 1;
  // otherwise the first optional step. Set once the queries settle.
  const [step, setStep] = useState<number | null>(null);
  useEffect(() => {
    if (step !== null || phoneQ.isLoading) return;
    // Opened from Settings on a specific step to edit it: honor it.
    if (initialStep != null) {
      navigatedRef.current = true;
      setStep(initialStep);
      return;
    }
    setStep(!initialConfig?.ownNumber ? 0 : !initialConfig?.forwardingConfirmedAt ? 1 : 2);
  }, [step, phoneQ.isLoading, initialConfig, initialStep]);

  // On the forwarding step without a shadow number yet, poll so the code appears on
  // its own the moment an admin assigns one — no manual refresh needed.
  const refetchPhone = phoneQ.refetch;
  useEffect(() => {
    if (step !== 1 || shadow) return;
    const id = setInterval(() => void refetchPhone(), 5000);
    return () => clearInterval(id);
  }, [step, shadow, refetchPhone]);

  // Form drafts, seeded from whatever is already saved (the resume case) and empty
  // otherwise. The wizard writes these to AssistantConfig as each step is left.
  // Defaults to the first carrier so the forwarding code is never empty on arrival;
  // the customer changes it on the forwarding step, next to the code it controls.
  const [carrier, setCarrier] = useState(
    initialConfig?.carrier ?? carriers[0]?.carrier ?? '',
  );
  // The no-answer timeout the customer types on the forwarding step. Kept as a
  // string so the field can be cleared while typing; ephemeral UI state — it is
  // substituted into the displayed dial code (the carrier owns the real value once
  // the customer dials it), so nothing persists it.
  const [forwardSeconds, setForwardSeconds] = useState<string>(String(DEFAULT_FORWARD_SECONDS));
  const [greetingName, setGreetingName] = useState(initialConfig?.greetingName ?? '');
  const [greetingScript, setGreetingScript] = useState(initialConfig?.greetingScript ?? '');
  const [voiceId, setVoiceId] = useState(initialConfig?.voiceId ?? '');
  const [autoSwitch, setAutoSwitch] = useState(initialConfig?.autoSwitchEnglish ?? true);
  const [customInstructions, setCustomInstructions] = useState(
    initialConfig?.customInstructions ?? '',
  );
  const [topics, setTopics] = useState<string[]>(initialConfig?.emergencyTopics ?? []);
  const [topicDraft, setTopicDraft] = useState('');
  // The customer's OWN personal number — the line they forward FROM and the one
  // click-to-call rings back on. We must capture it; nothing else does. E.164.
  const [ownNumber, setOwnNumber] = useState(initialConfig?.ownNumber ?? '');

  if (step === null) return null;

  const carrierRow = carriers.find((c) => c.carrier === carrier) ?? null;

  async function saveOwnNumber() {
    const v = ownNumber.trim();
    // Loose E.164-ish check: a leading + and 8–15 digits. Enough to catch a typo
    // without rejecting a valid international number.
    if (!/^\+?[0-9]{8,15}$/.test(v.replace(/[\s()-]/g, ''))) {
      toast.error(t('onboarding.number.invalid'));
      return;
    }
    try {
      await upsertConfig.mutateAsync({
        where: { accountId },
        create: { accountId, ownNumber: v },
        update: { ownNumber: v },
      } as Parameters<typeof upsertConfig.mutateAsync>[0]);
      setStep(1);
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  async function confirmForwarding() {
    try {
      await updateConfig.mutateAsync({
        where: { accountId },
        data: { carrier: carrier || null, forwardingConfirmedAt: new Date() },
      });
      setStep(2);
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  async function saveAssistant() {
    try {
      await updateConfig.mutateAsync({
        where: { accountId },
        data: {
          greetingName: greetingName.trim() || null,
          greetingScript: greetingScript.trim() || null,
          voiceId: voiceId || null,
          autoSwitchEnglish: autoSwitch,
          customInstructions: customInstructions.trim() || null,
        },
      } as Parameters<typeof updateConfig.mutateAsync>[0]);
      // Push the freshly saved config to the agent (durable, via Inngest). Best-
      // effort: the wizard advances regardless, and the recipe no-ops until an
      // admin has linked an agent to this account.
      void agentSync.mutateAsync({ args: { input: { accountId } } }).catch(() => {});
      setStep(3);
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  async function saveTopics() {
    try {
      await updateConfig.mutateAsync({ where: { accountId }, data: { emergencyTopics: topics } });
      setStep(4);
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  async function skipCurrent() {
    const current = step;
    const name = current === null ? undefined : STEPS[current];
    if (current === null || !name) return;
    try {
      await skip.mutateAsync({ args: { input: { accountId, step: name } } });
      setStep(current + 1);
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  // Finish (§3-А). `skipTest` records that the test call was skipped — the honest
  // state, since nothing here confirms a call actually arrived. The server refuses to
  // complete unless the line can receive one, so a green screen here means it really
  // can.
  async function finish(skipTest: boolean) {
    try {
      if (skipTest) await skip.mutateAsync({ args: { input: { accountId, step: 'TEST_CALL' } } });
      await complete.mutateAsync({ args: { input: { accountId } } });
      await queryClient.invalidateQueries();
      toast.success(t('onboarding.done.toast'));
      navigate(base, { replace: true });
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  function addTopic() {
    const v = topicDraft.trim();
    if (!v || topics.includes(v)) return setTopicDraft('');
    setTopics([...topics, v]);
    setTopicDraft('');
  }

  // The destination number comes from the account's shadow PhoneNumber row — empty
  // when none is assigned yet. The trailing `…<sec>#` of the template is swapped for
  // the customer's typed no-answer timeout, so editing the field rewrites the code.
  // While the field is empty the template's own value is kept, so the code never
  // shows a bare `#`.
  const secs = forwardSeconds.replace(/\D/g, '');
  const forwardingCode = carrierRow
    ? carrierRow.enableCode
        .replace('{number}', shadow?.e164 ?? '')
        .replace(/(\d+)#\s*$/, (_m, d) => `${secs || d}#`)
    : '';
  // The code to turn the forwarding OFF from the phone — shown next to the enable
  // code so the customer always has the way out. Usually a bare deactivation MMI
  // (e.g. ##61#) with no number, but replace {number} if the carrier's does.
  const disableCode = carrierRow
    ? carrierRow.disableCode.replace('{number}', shadow?.e164 ?? '')
    : '';

  return (
    <div className="min-h-dvh overflow-y-auto bg-background">
      <div className="mx-auto flex max-w-md flex-col px-4 py-8">
        {/* Escape hatches. "Skip for now" is always available: the shell no longer
            bounces a numberless owner into this wizard, so leaving it must always
            drop back to the app rather than trap them. Sign-out is always there too,
            so the wizard is never a dead end. */}
        <div className="mb-4 flex items-center justify-end gap-3">
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
            onClick={() => navigate(base)}
            disabled={busy}
          >
            {t('onboarding.skipForNow')}
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            title={t('action.signOut')}
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden />
            {t('action.signOut')}
          </button>
        </div>

        {/* Stepper: icon + label per step, arrows between, clickable — the same
            icons/labels as Settings → "This line". A tap jumps to that step to
            review or edit it (the server still enforces the mandatory rules). */}
        <nav className="mb-6 flex items-center gap-1">
          {STEP_NAV.map((n, i) => (
            <Fragment key={n.label}>
              <button
                type="button"
                aria-label={t(n.label)}
                aria-current={step === i ? 'step' : undefined}
                onClick={() => {
                  navigatedRef.current = true;
                  setStep(i);
                }}
                className="flex flex-1 flex-col items-center gap-1 text-center text-[11px] leading-tight"
              >
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                    step >= i ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  <n.icon className="h-4 w-4" aria-hidden />
                </span>
                {/* Labels only from `sm` up — on a phone the icons (with the active
                    one tinted) carry the step, and 4 BG labels wouldn't fit. */}
                <span
                  className={`hidden sm:block ${step === i ? 'font-medium text-foreground' : 'text-muted-foreground'}`}
                >
                  {t(n.label)}
                </span>
              </button>
              {/* Arrows only from `sm` up — hidden on phones so the icons sit evenly. */}
              {i < STEP_NAV.length - 1 ? (
                <ArrowRight
                  className="hidden h-3.5 w-3.5 shrink-0 text-muted-foreground sm:block sm:mb-4"
                  aria-hidden
                />
              ) : null}
            </Fragment>
          ))}
        </nav>

        {step === 0 && (
          <StepFrame
            title={t('onboarding.number.title')}
            subtitle={t('onboarding.number.subtitle')}
          >
            <label className="block">
              <span className="mb-1 block text-xs text-muted-foreground">
                {t('onboarding.number.mobile')}
              </span>
              <input
                className="input"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="+359..."
                value={ownNumber}
                onChange={(e) => setOwnNumber(e.target.value)}
              />
            </label>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {t('onboarding.number.note')}
            </p>
            <PrimaryButton onClick={saveOwnNumber} busy={busy}>
              {t('onboarding.number.submit')}
            </PrimaryButton>
          </StepFrame>
        )}

        {step === 1 && !shadow && (
          // Forwarding needs the internal (shadow) number, which an admin assigns.
          // Until then the customer can't do anything here — show a waiting state that
          // polls, rather than a broken code.
          <StepFrame
            title={t('onboarding.number.waitTitle')}
            subtitle={t('onboarding.number.waitSubtitle')}
          >
            <div className="card flex flex-col items-center gap-3 bg-muted/40 p-6 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Phone className="h-5 w-5 text-primary" />
              </span>
              <p className="text-sm text-muted-foreground">{t('onboarding.number.waitBody')}</p>
              <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                <span className="h-2 w-2 animate-pulse rounded-full bg-primary" />
                {t('onboarding.number.waitPolling')}
              </span>
            </div>
          </StepFrame>
        )}

        {step === 1 && shadow && (
          <StepFrame
            title={t('onboarding.forwarding.title')}
            subtitle={t('onboarding.forwarding.subtitle')}
          >
            <div className="flex items-center justify-center gap-2 py-2 text-center text-[11px] text-muted-foreground">
              <Diagram icon={<Phone className="h-4 w-4" />} label={t('onboarding.forwarding.yourPhone')} />
              <ArrowRight className="h-3.5 w-3.5 shrink-0" />
              <Diagram icon={<Clock className="h-4 w-4" />} label={t('onboarding.forwarding.noAnswer')} />
              <ArrowRight className="h-3.5 w-3.5 shrink-0" />
              <Diagram icon={<Bot className="h-4 w-4" />} label={t('onboarding.forwarding.assistant')} />
            </div>

            <label className="block">
              <span className="mb-1 block text-xs text-muted-foreground">
                {t('onboarding.forwarding.carrierLabel')}
              </span>
              <select
                className="input"
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
              >
                <option value="">{t('onboarding.forwarding.carrierPlaceholder')}</option>
                {carriers.map((c) => (
                  <option key={c.id} value={c.carrier}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-muted-foreground">
                {t('onboarding.forwarding.secondsLabel')}
              </span>
              <input
                className="input"
                type="number"
                inputMode="numeric"
                min={5}
                max={60}
                value={forwardSeconds}
                onChange={(e) => setForwardSeconds(e.target.value)}
              />
              <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                {t('onboarding.forwarding.secondsHint', { seconds: secs || DEFAULT_FORWARD_SECONDS })}
              </span>
            </label>
            <div className="card flex items-center justify-between gap-2 p-3">
              <code className="min-w-0 break-all font-mono text-sm text-foreground">
                {forwardingCode || '—'}
              </code>
              <button
                type="button"
                className="btn-secondary shrink-0 px-2 py-1"
                onClick={() => {
                  void navigator.clipboard?.writeText(forwardingCode);
                  toast.success(t('onboarding.forwarding.copied'));
                }}
              >
                <Copy className="mr-1 h-3.5 w-3.5" />
                {t('action.copy')}
              </button>
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {t('onboarding.forwarding.hint')}
            </p>
            <div className="space-y-1">
              <span className="block text-sm font-medium">
                {t('onboarding.forwarding.disable')}
              </span>
              <div className="card flex items-center justify-between gap-2 p-3">
                <code className="min-w-0 break-all font-mono text-sm text-foreground">
                  {disableCode || '—'}
                </code>
                <button
                  type="button"
                  className="btn-secondary shrink-0 px-2 py-1"
                  onClick={() => {
                    void navigator.clipboard?.writeText(disableCode);
                    toast.success(t('onboarding.forwarding.copied'));
                  }}
                >
                  <Copy className="mr-1 h-3.5 w-3.5" />
                  {t('action.copy')}
                </button>
              </div>
              <span className="block text-xs leading-relaxed text-muted-foreground">
                {t('onboarding.forwarding.disableHint')}
              </span>
            </div>
            <PrimaryButton onClick={confirmForwarding} busy={busy}>
              {t('onboarding.forwarding.submit')}
            </PrimaryButton>
          </StepFrame>
        )}

        {step === 2 && (
          <StepFrame
            title={t('onboarding.assistant.title')}
            subtitle={t('onboarding.assistant.subtitle')}
          >
            <label className="block">
              <span className="mb-1 block text-sm font-medium">
                {t('onboarding.assistant.name')}
              </span>
              <input
                className="input"
                value={greetingName}
                onChange={(e) => setGreetingName(e.target.value)}
                placeholder={t('onboarding.assistant.namePlaceholder')}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium">
                {t('onboarding.assistant.greeting')}
              </span>
              <textarea
                className="input min-h-24"
                rows={3}
                value={greetingScript}
                onChange={(e) => setGreetingScript(e.target.value)}
                placeholder={t('onboarding.assistant.greetingPlaceholder')}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium">
                {t('onboarding.assistant.voice')}
              </span>
              <select
                className="input"
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
              >
                <option value="">{t('onboarding.assistant.voicePlaceholder')}</option>
                {voices.map((v) => (
                  <option key={v.id} value={v.voiceId}>
                    {v.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium">
                {t('onboarding.assistant.instructions')}
              </span>
              <textarea
                className="input min-h-20"
                rows={3}
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                placeholder={t('onboarding.assistant.instructionsPlaceholder')}
              />
              <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                {t('onboarding.assistant.instructionsHint')}
              </span>
            </label>
            <Toggle
              checked={autoSwitch}
              onChange={setAutoSwitch}
              title={t('onboarding.assistant.autoSwitch')}
              sub={t('onboarding.assistant.autoSwitchSub')}
            />
            <StepActions
              onSkip={skipCurrent}
              onNext={saveAssistant}
              nextLabel={t('action.continue')}
              busy={busy}
            />
          </StepFrame>
        )}

        {step === 3 && (
          <StepFrame
            title={t('onboarding.topics.title')}
            subtitle={t('onboarding.topics.subtitle')}
          >
            <div className="flex gap-2">
              <input
                className="input"
                value={topicDraft}
                onChange={(e) => setTopicDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTopic();
                  }
                }}
                placeholder={t('onboarding.topics.placeholder')}
              />
              <button type="button" className="btn-secondary shrink-0" onClick={addTopic}>
                {t('action.add')}
              </button>
            </div>
            {topics.length > 0 && (
              <ul className="flex flex-wrap gap-2">
                {topics.map((tp) => (
                  <li
                    key={tp}
                    className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm"
                  >
                    {tp}
                    <button
                      type="button"
                      onClick={() => setTopics(topics.filter((x) => x !== tp))}
                      className="text-muted-foreground hover:text-foreground"
                      aria-label={t('action.remove')}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <StepActions
              onSkip={skipCurrent}
              onNext={saveTopics}
              nextLabel={t('action.continue')}
              busy={busy}
            />
          </StepFrame>
        )}

        {step === 4 && (
          <div className="flex flex-col items-center gap-4 pt-10 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Check className="h-7 w-7 text-primary" />
            </span>
            <h1 className="text-xl font-semibold">{t('onboarding.done.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('onboarding.done.subtitle')}</p>
            {/* One action: mark setup complete and drop the customer on their calls
                screen (the account index). No test-call / skip options. */}
            <PrimaryButton onClick={() => finish(true)} busy={busy}>
              {t('onboarding.done.goToCalls')}
            </PrimaryButton>
          </div>
        )}
      </div>
    </div>
  );
}

function StepFrame({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold">{title}</h1>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

function PrimaryButton({
  onClick,
  busy,
  children,
}: {
  onClick: () => void;
  busy: boolean;
  children: React.ReactNode;
}) {
  return (
    <button type="button" className="btn-primary w-full" onClick={onClick} disabled={busy}>
      {children}
    </button>
  );
}

/** Optional step footer: skip on the left, continue on the right. Only the optional
 *  steps render this — the mandatory ones offer no skip at all. */
function StepActions({
  onSkip,
  onNext,
  nextLabel,
  busy,
}: {
  onSkip: () => void;
  onNext: () => void;
  nextLabel: string;
  busy: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 pt-1">
      <button
        type="button"
        className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
        onClick={onSkip}
        disabled={busy}
      >
        {t('action.skip')}
      </button>
      <button type="button" className="btn-primary flex-1" onClick={onNext} disabled={busy}>
        {nextLabel}
      </button>
    </div>
  );
}

function Diagram({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="flex flex-1 flex-col items-center gap-1">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-foreground">
        {icon}
      </span>
      <span className="leading-tight">{label}</span>
    </span>
  );
}

function Toggle({
  checked,
  onChange,
  title,
  sub,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  title: string;
  sub: string;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3">
      <span className="min-w-0">
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-xs text-muted-foreground">{sub}</span>
      </span>
      <span className="relative inline-flex shrink-0">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="h-6 w-10 rounded-full bg-muted transition-colors peer-checked:bg-primary" />
        <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-background transition-transform peer-checked:translate-x-4" />
      </span>
    </label>
  );
}
